import type {
  ComfyHistory,
  JobKind,
  JobRecord,
  QueuePromptSummary,
  ServerRuntime,
} from '../types'

const JST_TIME = new Intl.DateTimeFormat('ja-JP', {
  timeZone: 'Asia/Tokyo',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
})

export function displayIdentity(identity: string): string {
  const value = identity.trim()
  if (!value || value === 'local') return 'Local user'
  const at = value.indexOf('@')
  return at > 0 ? value.slice(0, at) : value
}

export function sourceStem(sourceName: string): string {
  const filename = sourceName.trim().split(/[\\/]/).at(-1) ?? ''
  return filename.replace(/\.[^.\\/]+$/, '') || filename
}

export function sourceDisplayName(
  sourceName: string | undefined,
  jobKind?: JobKind,
  sourceCount = 1,
): string {
  if (!sourceName) return ''
  const base = sourceStem(sourceName)
  return jobKind === 'concat' && sourceCount > 1
    ? `${base} + ${sourceCount - 1}本`
    : base
}

const batchSuffix = (batchIndex?: number, batchTotal?: number): string =>
  batchTotal && batchTotal > 1 && batchIndex && batchIndex > 0
    ? ` [${batchIndex}/${batchTotal}]`
    : ''

export function formatJstTime(timestamp: number): string {
  return JST_TIME.format(new Date(timestamp))
}

export function jobDisplayTitle(job: JobRecord, fallbackNumber: number): string {
  const source = sourceDisplayName(job.sourceName, job.jobKind, job.sourceCount)
  const base = source || job.label || `Job #${fallbackNumber}`
  const batch = source ? batchSuffix(job.batchIndex, job.batchTotal) : ''
  const completed = Number.isFinite(job.completedAt)
    ? `・${formatJstTime(job.completedAt!)}`
    : ''
  return `${base}${batch}${completed}`
}

export function queuePromptDisplay(summary: QueuePromptSummary): string {
  const source = sourceDisplayName(summary.sourceName, summary.jobKind, summary.sourceCount)
  const fallback = summary.product === 'h3' ? 'H3 Job' : 'ComfyUI Job'
  return `${source || fallback}${batchSuffix(summary.batchIndex, summary.batchTotal)}`
}

export function queueOwnerDisplay(summary: QueuePromptSummary): string {
  if (summary.submittedBy) return summary.submittedBy
  return summary.product === 'h3' ? 'Unknown / H3' : 'Unknown / ComfyUI'
}

export function serverStatusText(runtime: ServerRuntime | undefined): string {
  if (!runtime || runtime.status === 'disconnected') return 'Not connected'
  if (runtime.status === 'connecting') return 'Checking…'
  if (runtime.status === 'offline') return 'Offline'
  if (runtime.status === 'idle') return 'Idle'
  return runtime.pending > 0 ? `Busy・${runtime.pending} 待ち` : 'Busy'
}

export function completionTimeFromHistory(
  history: ComfyHistory,
  promptId: string,
): number | undefined {
  const entry = history[promptId]
  if (!entry?.status || typeof entry.status !== 'object') return undefined
  const messages = (entry.status as { messages?: unknown }).messages
  if (!Array.isArray(messages)) return undefined
  for (const message of [...messages].reverse()) {
    if (!Array.isArray(message) || message[0] !== 'execution_success') continue
    const details = message[1]
    if (!details || typeof details !== 'object') continue
    const timestamp = (details as { timestamp?: unknown }).timestamp
    if (typeof timestamp === 'number' && Number.isFinite(timestamp)) return timestamp
  }
  return undefined
}
